import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { List } from '@material-ui/core';
import { createStyles, withStyles, WithStyles, WithTheme } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';

import { Group } from 'api';
import { GroupAvatar, StickySidebar, SidebarItem, UserAvatar, CustomSVG } from 'components';
import { Groups, Index, AdminPosts, generateLink } from 'Links';
import { useCurrentUser } from 'hooks';
import { isAdminUser } from 'helpers';

const styles = () =>
  createStyles({
    addIcon: {
      fontSize: 16,
    },
  });

interface IProps {
  groups: Group[];
  isHomeSelected: () => boolean;
  isGroupSelected: (group: Group) => boolean;
  isAllPostsSelected: () => boolean;
}

interface IPropsEnhanced extends IProps, WithTheme, WithStyles<typeof styles> {}

const ICON_SIZE = 20;

const SelectDesktop: FC<IPropsEnhanced> = props => {
  const { classes, theme, groups, isHomeSelected, isGroupSelected, isAllPostsSelected } = props;
  const { t } = useTranslation();
  const currentUser = useCurrentUser();

  function renderHome() {
    const selectedColor = theme.palette.secondary.main;
    const icon = <UserAvatar size={ICON_SIZE} user={currentUser} />;

    return (
      <SidebarItem
        color="inherit"
        id={currentUser.id}
        link={Index}
        isSelected={isHomeSelected()}
        textNode={t('components:timeline')}
        selectedColor={selectedColor}
        icon={icon}
      />
    );
  }

  function renderGroup(group: Group) {
    const isSelected = isGroupSelected(group);
    const selectedColor = group.color_map.hex;

    return (
      <SidebarItem
        id={group.id}
        key={group.id}
        color="inherit"
        link={generateLink(`/group/${group.id}`)}
        isSelected={isSelected}
        textNode={group.title}
        selectedColor={selectedColor}
        icon={<GroupAvatar size={ICON_SIZE} group={group} />}
      />
    );
  }

  function renderAdminAllPosts() {
    if (!isAdminUser(currentUser)) {
      return null;
    }

    const selectedColor = theme.palette.secondary.main;
    const isSelected = isAllPostsSelected();

    return (
      <SidebarItem
        color="inherit"
        link={AdminPosts}
        isSelected={isSelected}
        textNode={t('admin:allPosts')}
        selectedColor={selectedColor}
        icon={<CustomSVG color="secondary" size={ICON_SIZE} name="home" />}
      />
    );
  }

  function renderDiscover() {
    return (
      <SidebarItem
        color="inherit"
        link={Groups}
        isSelected={false}
        icon={<AddIcon className={classes.addIcon} />}
        textNode={t('feed:discoverGroups')}
      />
    );
  }

  return (
    <StickySidebar>
      <List component="nav">
        {renderHome()}
        {renderAdminAllPosts()}
        {groups.map(renderGroup)}
        {renderDiscover()}
      </List>
    </StickySidebar>
  );
};

export default withStyles(styles, { withTheme: true })(SelectDesktop);
